import { Graph, Node, Link, AnchorType, Anchor } from "@livereader/graphly-d3";
import type { ConceptDataobject, TemplateDataobject } from "@lr-notitia/notitia-model-store/schemas";
import type { TemplateProcess, TemplateWorkspace } from "@lr-notitia/notitia-designer/schemas";

import { useGraphStore } from "../../../store/graph";
import { useTemplateProcessesStore } from "../../../store/t-process";
import { useTemplateWorkspacesStore } from "../../../store/t-workspace";
import { useTeamlyStore } from "../../../store/teamly";
import { useGraphlyTemplatesStore } from "../../../store/graphly-templates";
import { useSymbolsStore } from "../../../store/symbols";

export type AttributeId = string;

let graphStore: ReturnType<typeof useGraphStore>;
let processesStore: ReturnType<typeof useTemplateProcessesStore>;
let workspacesStore: ReturnType<typeof useTemplateWorkspacesStore>;
let teamlyStore: ReturnType<typeof useTeamlyStore>;
let graphlyTemplatesStore: ReturnType<typeof useGraphlyTemplatesStore>;
let symbolsStore: ReturnType<typeof useSymbolsStore>;
function init() {
	graphStore = useGraphStore();
	processesStore = useTemplateProcessesStore();
	workspacesStore = useTemplateWorkspacesStore();
	if (!teamlyStore) teamlyStore = useTeamlyStore();
	graphlyTemplatesStore = useGraphlyTemplatesStore();
	symbolsStore = useSymbolsStore();
}

export function graphlyGraph(workspaceId: string): Graph {
	if (!graphStore || !workspacesStore || !teamlyStore) init();
	const workspace = workspacesStore.getById(workspaceId);
	if (!workspace) return { nodes: [], links: [] };
	const nodes: Node[] = [];
	const links: Link[] = [];

	const dataobjects: TemplateDataobject[] = dataobjectsInWorkspace(workspace.id);
	dataobjects
		.map((d) => graphStore.template.dataobject.byId(d.id))
		.filter((d) => d !== undefined)
		.forEach((dataobject) => {
			nodes.push(dataobjectNode(dataobject as TemplateDataobject, workspace));
		});

	dataobjects.forEach((dataobject: TemplateDataobject) => {
		dataobject.hasA.forEach((hasAId) => {
			const hasA = graphStore.template.dataobject.byId(hasAId);
			if (!hasA) return;
			links.push(createLink(dataobject, hasA));
		});
	});
	return { nodes, links };
}

function dataobjectsInWorkspace(id: string): TemplateDataobject[] {
	const workspace = workspacesStore.getById(id);
	if (!workspace) return [];
	const dataobjects = [...workspace.dataobjects];
	dataobjects.forEach((d) => {
		let c = childrenInWorkspace(d, workspace).filter((c) => !dataobjects.includes(c));
		dataobjects.push(...c);
		c = [];
	});
	return dataobjects
		.filter((d, i, a) => a.indexOf(d) === i)
		.map((d) => graphStore.template.dataobject.byId(d))
		.filter((d) => d !== undefined) as TemplateDataobject[];
}
function childrenInWorkspace(dataobjectId: string, workspace: TemplateWorkspace): string[] {
	if (!workspace.dataobjects.includes(dataobjectId)) return [];
	const dataobjects = childrenDataobjects(dataobjectId);
	dataobjects.forEach((d) => {
		let c = childrenInWorkspace(d, workspace).filter((c) => !dataobjects.includes(c));
		dataobjects.push(...c);
		c = [];
	});
	return dataobjects;
}

function createLink(source: TemplateDataobject, target: TemplateDataobject): Link {
	return {
		id: `${source.id}_${target.id}`,
		label: `[1..1] to [${target.cardinality?.min || "0"}..${
			target.cardinality?.max ? (target.cardinality.max === -1 ? "infinite" : target.cardinality.max) : "infinite"
		}]`,
		curvature: 0.05,
		strength: 0,
		width: 3,
		directed: true,
		source: source.id,
		target: target.id,
	} as Link;
}
function dataobjectNode(dataobject: TemplateDataobject, workspace: TemplateWorkspace): Node {
	if (!graphStore || !processesStore || !workspacesStore || !symbolsStore) init();
	let concept: ConceptDataobject | undefined = graphStore.concept.dataobject.byId(dataobject.concept);
	return {
		id: dataobject.id,
		shape: {
			type: "data-object",
			scale: scale(dataobject, workspace, 1),
			url: graphlyTemplatesStore.dataobjectShape,
		},
		...position(dataobject, workspace),
		payload: {
			title: title(dataobject),
			tags: dataobject.processes
				? dataobject.processes.map((processId: any) =>
						symbolsStore.term({
							id: processesStore.getById(processId)?.refersTo || "",
							terminology: processesStore.getById(processId)?.terminology || {},
						})
				  )
				: [],
			type: "collection",
			color: color(concept),
		},
	};
}
function title(dataobject: TemplateDataobject): string {
	let templateTerm = symbolsStore.term({ id: dataobject.refersTo, terminology: dataobject.terminology || {} });
	if (templateTerm && templateTerm !== dataobject.refersTo) return templateTerm;
	let concept = graphStore.concept.dataobject.byId(dataobject.concept);
	if (!concept) return "";
	let conceptTerm = symbolsStore.term({ id: concept.refersTo, terminology: concept.terminology || {} });
	if (conceptTerm && conceptTerm !== concept.refersTo) return conceptTerm;
	return "";
}
function position(
	DO: TemplateDataobject,
	workspace: TemplateWorkspace
): { x: number | undefined; y: number | undefined; anchor: Anchor | undefined } {
	if (!workspacesStore) init();
	const pos = workspacesStore.nodePositions[workspace.id]?.[DO.id];
	if (pos) {
		return {
			x: pos.x,
			y: pos.y,
			anchor: { type: AnchorType.Soft, x: pos.x, y: pos.y },
		};
	} else {
		return {
			x: undefined,
			y: undefined,
			anchor: undefined,
		};
	}
}
function scale(
	node: TemplateDataobject | TemplateProcess,
	workspace: TemplateWorkspace,
	fallback: number = 1
): number {
	const scale = workspacesStore.nodePositions[workspace.id]?.[node.id]?.scale;
	return scale ? scale : fallback ? fallback : 1;
}
function composedTemplateAttributes(dataobject: TemplateDataobject): AttributeId[] {
	if (!graphStore || !processesStore) init();
	if (!dataobject) return [];
	return childrenAttributes(dataobject.id, -1)
		.map((t: any) => graphStore.concept.attribute.byId(t)?.id)
		.flat()
		.filter((t: any) => t !== undefined) as AttributeId[];
}

function childrenAttributes(dataobjectId: string, depth = 0): string[] {
	if (!graphStore || !processesStore) init();
	const dataobject = graphStore.concept.dataobject.byId(dataobjectId);
	if (!dataobject) return [];
	const attributes: string[] = dataobject.consistsOf ? [...dataobject.consistsOf] : [];
	if (depth != 0) {
		attributes.forEach((d) => {
			attributes.push(...childrenAttributes(d, depth - 1).filter((c) => !attributes.includes(c)));
		});
	}
	return attributes;
}
function childrenDataobjects(dataobjectId: string): string[] {
	if (!graphStore || !processesStore) init();
	const dataobject = graphStore.template.dataobject.byId(dataobjectId);
	if (!dataobject) return [];
	const dataobjects: string[] = [...dataobject.hasA];
	dataobjects.forEach((d) => {
		dataobjects.push(...childrenDataobjects(d).filter((c) => !dataobjects.includes(c)));
	});
	return dataobjects;
}

function color(concept: ConceptDataobject | undefined) {
	return concept?.color ? concept.color : "#27282B";
}
